'use client'

import { CustomButtonNew, CustomTooltip, HamburgerMenu } from '@/components'
import { HamMenuIcon } from '@/screens/home/components/HamMenuIcon'
import { Button, Flex } from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import { REDIRECT_AFTER_LOGIN_COOKIE } from '@partment/types'
import { setCookie } from 'cookies-next'
import Image from 'next/image'
import Link from 'next/link'
import ListingSearch from '@/components/searchBox/ListingSearch'
import { useNavBar } from './useNavBar'
import partment_stay_logo_property_details from 'public/images/Partment_logo_from_property_details.svg'
import { onHandleLogout } from '@/utils/logout'
import { LuLogOut } from 'react-icons/lu'


function redirectToLogin() {
  const pathname = `${globalThis.location.pathname}?${(() => {
    const p = new URLSearchParams(globalThis.location.search)
    p.set('source', 'stays')
    return p.toString()
  })()}`

  setCookie(REDIRECT_AFTER_LOGIN_COOKIE, pathname, {
    path: '/',
    secure: process.env.NODE_ENV === 'production',
    sameSite: 'lax',
  })
}

const Navbar = ({
  loading = false,
  CheckOut = false,
  isPropertyDetailPage = false,
  listShowing = false,
  headphone = false,
}) => {
  const [opened, { open, close }] = useDisclosure(false)

  const {
    isLogin,
    mounted,
    active,
    setActive,
    desktopMenuRef,
    handleClick,
    scrollToSection,
  } = useNavBar()

  return (
    <nav className='relative mx-auto px-3 md:px-8'>
      <div
        className={`py-5 ${isPropertyDetailPage ? 'md:py-4' : 'md:py-4'} flex items-center justify-between gap-4`}
      >
        {/* Logo */}
        <div className='flex-shrink-0'>
          <Flex gap='lg'>
            <Link href='/' className='cursor-pointer'>
              <Image
                src={partment_stay_logo_property_details}
                alt='partment_stay_logo_property_details'
                width={112}
                height={36}
                priority
                className='object-contain w-25 md:w-30'
              />
            </Link>

            {/* Desktop Menu */}
            {isPropertyDetailPage && (
              <div className='items-center hidden md:flex font-medium'>
                <div
                  ref={desktopMenuRef}
                  className='hidden md:flex items-center gap-3'
                >
                  <button
                    onClick={() => handleClick('testimonials')}
                    className={`cursor-pointer !text-[13px] rounded-lg transition text-[#191919] ${active === 'testimonials' ? 'bg-[#F7F7F7] px-3 py-1' : ''
                      }`}
                  >
                    Testimonials
                  </button>
                  <Link
                    href='/list-your-stay'
                    onClick={() => setActive('list')}
                    className='cursor-pointer flex items-center justify-center gap-[10px] h-[32px] py-1 px-3 rounded-[7px] bg-[#F7F7F7] font-medium transition-none opacity-100'
                  >
                    <span className='font-medium text-[13px] leading-[24px] tracking-normal text-[#191919]'>
                      List Your Property
                    </span>
                  </Link>
                </div>
              </div>
            )}
          </Flex>
        </div>

        {/* Search (desktop) — between logo and auth buttons */}
        {listShowing && (
          <div className='hidden md:block flex-1 max-w-4xl mx-auto'>
            <ListingSearch />
          </div>
        )}

        {/* Right Menu */}
        <div className='items-center gap-2 hidden md:flex flex-shrink-0'>
          {mounted && (
            <>
              {isLogin ? (
                <>
                  <Button
                    component='a'
                    href='/global-portal'
                    radius='sm'
                    bg='#000'
                    c='#fff'
                    size='xs'
                  >
                    Dashboard
                  </Button>
                  <Button
                    component='a'
                    href='/stays/my-booking'
                    radius='sm'
                    bg='#000'
                    c='#fff'
                    size='xs'
                  >
                    My Bookings
                  </Button>
                  <CustomTooltip position='bottom' content={'Log out'} bg='bg-gray-500' text='text-white'>
                    <CustomButtonNew
                      onClick={onHandleLogout}
                      ariaLabel='profile button'
                      className='p-3 text-lg font-semibold text-white bg-transparent border-2 border-white rounded-full md:text-xl hover:bg-white hover:text-azure_blue'
                      label={<LuLogOut />}
                      bg='#000'
                      c='#fff'
                    />
                  </CustomTooltip>
                </>
              ) : (
                <>
                  <Button
                    component='a'
                    href='/sso/login'
                    radius='sm'
                    bg='#000'
                    c='#fff'
                    size='xs'
                    onClick={redirectToLogin}
                  >
                    Login
                  </Button>
                  <Button
                    component='a'
                    href='/sso/signup'
                    radius='sm'
                    c='#fff'
                    bg='#000'
                    size='xs'
                    onClick={redirectToLogin}
                  >
                    Register
                  </Button>
                </>
              )}
            </>
          )}
        </div>

        {/* Mobile Menu Button */}
        <div className='flex gap-2 md:hidden'>
          {mounted && !isLogin && (
            <>
              <Button
                component='a'
                href='/sso/login'
                radius='sm'
                bg='#000'
                c='#fff'
                size='xs'
                onClick={redirectToLogin}
              >
                Login
              </Button>
              <Button
                component='a'
                href='/sso/signup'
                radius='sm'
                c='#fff'
                bg='#000'
                size='xs'
                onClick={redirectToLogin}
              >
                Register
              </Button>
            </>
          )}
          <Button size='xs' c='#fff' bg='#000' onClick={open}>
            <span className='text-white text-xl'>
              <HamMenuIcon style={{ color: 'red' }} />
            </span>
          </Button>
          {mounted && isLogin && (
            <CustomTooltip position='bottom' content={'Log out'} bg='bg-gray-500' text='text-white'>
              <CustomButtonNew
                onClick={onHandleLogout}
                ariaLabel='profile button'
                className='p-3 text-lg font-semibold text-white bg-transparent border-2 border-white rounded-full md:text-xl hover:bg-white hover:text-azure_blue'
                label={<LuLogOut />}
                bg='#000'
                c='#fff'
              />
            </CustomTooltip>
          )}
        </div>
      </div>

      {/* Mobile Search */}
      {listShowing && (
        <div className='md:hidden max-w-[1200px] w-full mb-4'>
          <ListingSearch />
        </div>
      )}
      {!listShowing && CheckOut && (
        <div className='md:hidden max-w-[1200px] w-full mb-4'>
          <ListingSearch />
        </div>
      )}

      <HamburgerMenu
        close={close}
        opened={opened}
        scrollToSection={scrollToSection}
        mounted={mounted}
        isLogin={isLogin}
      />
    </nav>
  )
}

export default Navbar

const Btn = ({ onClick, children, className = '' }) => {
  return (
    <button
      type='button'
      onClick={onClick}
      className={` text-black py-2 px-3 md:px-5 text-sm font-medium rounded-lg cursor-pointer border border-black ${className}`}
    >
      {children}
    </button>
  )
}
